import React from "react";
import MedicalConditionCard from "./MedicalConditionCard";
import { hasDisplayValue, isStatusTrue } from "../utils/patientUtils";
import {
  SUMMARY_SPACING,
  SummaryEmpty,
  summaryInnerCard,
  summaryLabelClass,
} from "./patientSummaryUi";
import type { SummaryTheme } from "./patientSummaryUi";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

interface MedicalTabProps {
  patientData: any;
  theme?: SummaryTheme;
}

type DrugBlock = {
  label: string;
  statusTrue: boolean;
  hasItems: boolean;
  items: any[];
  note: string;
  columns: Array<{ key: string; label: string; value: (item: any) => string }>;
};

function DrugAlertCard({ block, theme }: { block: DrugBlock; theme: SummaryTheme }) {
  if (!block.statusTrue) return null;
  const isDark = theme === "dark";

  return (
    <div className={summaryInnerCard(theme, true)}>
      <div className="mb-1.5 flex items-start gap-2">
        <WarningAmberOutlinedIcon
          className={isDark ? "text-red-400" : "text-red-500"}
          sx={{ fontSize: 17 }}
        />
        <p className={`text-sm font-semibold ${isDark ? "text-red-200" : "text-red-800"}`}>{block.label}</p>
      </div>
      {block.hasItems && (
        <div className="mt-1.5 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className={summaryLabelClass(theme)}>
                {block.columns.map((col) => (
                  <th key={col.key} className="pr-3 py-0.5 text-left font-semibold">
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className={isDark ? "text-red-200/90" : "text-red-900/80"}>
              {block.items.map((item, i) => (
                <tr key={item?.id ?? i}>
                  {block.columns.map((col) => (
                    <td key={col.key} className="py-0.5 pr-3">
                      {col.value(item)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {block.note && (
        <p className={`mt-1.5 text-xs leading-relaxed ${isDark ? "text-red-300/90" : "text-red-700"}`}>
          {block.note}
        </p>
      )}
    </div>
  );
}

const MedicalTab: React.FC<MedicalTabProps> = ({ patientData, theme = "light" }) => {
  const medicalConditions = [
    { key: "gastricProblem", label: "Gastric Problem" },
    { key: "highBP", label: "High BP" },
    { key: "diabetes", label: "Diabetes" },
    { key: "monthlyInjection", label: "Monthly Injection" },
    { key: "pregnancyBreastFeeding", label: "Pregnancy / Breast Feeding" },
    { key: "coughColdFever", label: "Cough / Cold / Fever" },
    { key: "chestpainHeartProblem", label: "Chest Pain / Heart" },
    { key: "epilepsy", label: "Epilepsy" },
    { key: "previousExtraction", label: "Previous Extraction" },
    { key: "previoussurgeries", label: "Previous Surgeries" },
    { key: "covid193Doses", label: "COVID-19 (3 Doses)" },
    { key: "drugHistory", label: "Drug History" },
    { key: "dietaryHistory", label: "Dietary History" },
    { key: "brushingHistory", label: "Brushing History" },
    { key: "medicalHistoryNote", label: "Medical History Note" },
    { key: "oralHygieneHistory", label: "Oral Hygiene" },
    { key: "habitHistory", label: "Habits" },
    { key: "familyHistory", label: "Family History" },
    { key: "socialHistory", label: "Social History" },
  ];

  const activeConditions = medicalConditions.filter((c) => {
    const data = patientData?.[c.key] || {};
    return isStatusTrue(data?.status) || hasDisplayValue(data?.note);
  });

  const buildDrugBlock = (label: string, source: any, columns: DrugBlock["columns"]): DrugBlock => ({
    label,
    statusTrue: isStatusTrue(source?.status),
    hasItems: Array.isArray(source?.items) && source.items.length > 0,
    items: Array.isArray(source?.items) ? source.items : [],
    note: source?.note ? String(source.note).trim() : "",
    columns,
  });

  const drugBlocks = [
    buildDrugBlock("Allergies", patientData?.allergies, [
      { key: "type", label: "Type", value: (i) => i?.label ?? i?.id ?? "—" },
      { key: "note", label: "Note", value: (i) => (i?.note ? String(i.note) : "—") },
      { key: "dose", label: "Dosage", value: (i) => (i?.dosage ? String(i.dosage) : "—") },
    ]),
    buildDrugBlock("Diabetes Drugs", patientData?.diabetesMellitusDrugs, [
      { key: "drug", label: "Drug", value: (i) => i?.label ?? i?.id ?? "—" },
      { key: "note", label: "Note", value: (i) => (i?.note ? String(i.note) : "—") },
    ]),
    buildDrugBlock("Hypertension Drugs", patientData?.hypertensionDrugs, [
      { key: "drug", label: "Drug", value: (i) => i?.label ?? i?.id ?? "—" },
      { key: "note", label: "Note", value: (i) => (i?.note ? String(i.note) : "—") },
    ]),
    buildDrugBlock("Dyslipidemia", patientData?.dyslipidemiaDrugs, [
      { key: "drug", label: "Drug", value: (i) => i?.label ?? i?.id ?? "—" },
      { key: "note", label: "Note", value: (i) => (i?.note ? String(i.note) : "—") },
    ]),
  ].filter((b) => b.statusTrue);

  const examNotes = [
    { label: "Extra Oral", value: patientData?.extraOralExamination },
    { label: "Intra Oral", value: patientData?.intraOralExamination },
  ].filter((f) => hasDisplayValue(f.value));

  if (drugBlocks.length === 0 && activeConditions.length === 0 && examNotes.length === 0) {
    return <SummaryEmpty message="No medical alerts on record." theme={theme} />;
  }

  return (
    <div className={SUMMARY_SPACING.innerStack}>
      {(drugBlocks.length > 0 || activeConditions.length > 0) && (
        <div className={`grid grid-cols-1 md:grid-cols-2 ${SUMMARY_SPACING.innerGrid}`}>
          {drugBlocks.map((block) => (
            <DrugAlertCard key={block.label} block={block} theme={theme} />
          ))}
          {activeConditions.map((c) => (
            <MedicalConditionCard
              key={c.key}
              label={c.label}
              condition={patientData?.[c.key] || {}}
              theme={theme}
              hideWhenInactive
            />
          ))}
        </div>
      )}
      {examNotes.length > 0 && (
        <div className={`grid grid-cols-1 sm:grid-cols-2 ${SUMMARY_SPACING.innerGrid}`}>
          {examNotes.map((f) => (
            <div key={f.label} className={summaryInnerCard(theme)}>
              <p className={`mb-0.5 ${summaryLabelClass(theme)}`}>{f.label}</p>
              <p className={`text-sm leading-relaxed ${theme === "dark" ? "text-slate-200" : "text-slate-800"}`}>
                {f.value}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MedicalTab;
